import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useMobileLayout, useOverflow } from "../../hooks";
import { createTheme, ThemeOptions, ThemeProvider, Tooltip, TooltipProps } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { themeOptions } from "../../theme";
import styles from "./index.module.css";

type ScrollbarMode = "light" | "dark";

const scrollbarStyleThemeOverrides = {
	"&::-webkit-scrollbar-track": {
		background: "transparent",
		boxShadow: "none",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "var(--scrollbar-background)",
		outline: "none",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background: "var(--scrollbar-background-hover)",
	},
	"&::-webkit-scrollbar-thumb:active": {
		background: "var(--scrollbar-background-active)",
	},
	"&::webkit-scrollbar-track-piece:start": {
		background: "transparent",
	},
	"&::webkit-scrollbar-track-piece:end": {
		background: "transparent",
	},
};

function getScrollbarColourThemeOverrides(mode: ScrollbarMode = "light") {
	return {
		"--scrollbar-background": mode === "light" ? "#E8E8E8" : "#8C8C8C",
		"--scrollbar-background-hover": mode === "light" ? "#BABABA" : "#808080",
		"--scrollbar-background-active": mode === "light" ? "#7C7C7C" : "#696969",
	};
}
function getScrollbarThicknessThemeOverrides(thickness: string | number = 10) {
	return {
		"&::-webkit-scrollbar": {
			width: thickness,
			height: thickness,
		},
		"&::-webkit-scrollbar-thumb": {
			borderRadius: thickness,
		},
	};
}

const tooltipTheme: ThemeOptions["components"] = {
	MuiTooltip: {
		defaultProps: {
			arrow: true,
		},
		styleOverrides: {
			popper: {
				maxWidth: 525,
				maxHeight: 65,
			},
			tooltip: {
				fontSize: 14,
				fontWeight: 500,
				background: "#4C4C4C",
				"*": deepmerge(
					scrollbarStyleThemeOverrides,
					deepmerge(getScrollbarThicknessThemeOverrides(7), getScrollbarColourThemeOverrides("dark")),
				),
			},
			arrow: {
				color: "#4C4C4C",
			},
		},
	},
};

export default function EllipsisTooltip({
	children,
	entry = children,
	width,
	offset,
	arrowOffset,
	disablePortal,
	...restTooltipProps
}: {
	children: ReactElement;
	entry?: React.ReactNode;
	width?: number;
	offset?: [number, number];
	arrowOffset?: [number, number];
	disablePortal?: boolean;
} & Partial<Omit<TooltipProps, "children" | "title" | "classes" | "PopperProps" | "componentsProps">>) {
	const [textRef, isOverflowed] = useOverflow();
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [isTall, setIsTall] = useState(false);
	const isMobileLayout = useMobileLayout();
	const localTheme = createTheme({ ...themeOptions, ...tooltipTheme });

	useEffect(() => {
		if (measureRef.current) {
			setIsTall(measureRef.current.scrollHeight > 47);
		}
	}, [entry]);

	const childWithEllipsis = React.cloneElement(children, {
		className: `${children.props.className ?? ""} ${styles.ellipsis}`.trim(),
	});

	return (
		<ThemeProvider theme={localTheme}>
			<div
				ref={(node) => {
					measureRef.current = node;
					textRef(node);
				}}
				className={styles.tooltipMeasure}
				style={{ maxWidth: isMobileLayout ? undefined : `${width ?? 500}px` }}
			>
				{entry}
			</div>

			{isOverflowed ? (
				<Tooltip
					title={<div className={styles.tooltipScrollContent}>{entry}</div>}
					arrow
					placement="top"
					{...restTooltipProps}
					classes={{
						tooltip: isTall ? styles.customTooltipLong : styles.customTooltip,
					}}
					PopperProps={{
						disablePortal: disablePortal ?? true,
						modifiers: [
							{ name: "flip", enabled: false },
							{ name: "preventOverflow", enabled: false },
							{
								name: "offset",
								options: { offset: offset ?? [0, 0] },
							},
						],
					}}
					componentsProps={{
						tooltip: {
							style: { width: `${width ?? 500}px` },
						},
						arrow: {
							style: arrowOffset ? { marginLeft: `${arrowOffset[0]}px`, marginTop: `${arrowOffset[1]}px` } : {},
						},
					}}
				>
					{childWithEllipsis}
				</Tooltip>
			) : (
				<Tooltip {...restTooltipProps} title={entry}>
					{childWithEllipsis}
				</Tooltip>
			)}
		</ThemeProvider>
	);
}
